import ApiService from 'utils/ApiCalls';
import { LanguageList, IFilters } from '../globalTypes';
import { Dispatch } from '@reduxjs/toolkit';
import { notification } from 'antd';
import managementSlice from './managementSlice';
import Helpers from 'utils/Helpers';
import { ICategoryResult, IProductResult, IAuditFindingsResult } from './types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';

interface IDayjsObject {
  $d: Date;
  [key: string]: unknown;
}

interface IEntityProps {
  [key: string]: number;
}

// Helper function to format date to YYYY-MM-DD without timezone conversion
const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Cache for owner data
let ownersCache: { [key: string]: string } | null = null;

// Helper function to fetch and cache owner data
const fetchOwners = async (): Promise<{ [key: string]: string }> => {
  if (ownersCache) return ownersCache;

  try {
    const response = await ApiService.getOwnersList();
    const ownerMap: { [key: string]: string } = {};

    if (response?.data?.ownership && Array.isArray(response.data.ownership)) {
      response.data.ownership.forEach((owner: any) => {
        const id = owner.per_person_id;
        const email = owner.per_email;

        if (id && email) {
          ownerMap[id.toString()] = email;
        }
      });
    }

    ownersCache = ownerMap;
    return ownerMap;
  } catch (error) {
    console.error('Error fetching owners:', error);
    return {};
  }
};

// Helper function to map owner ID to email
const getOwnerEmail = async (ownerId: number | string): Promise<string> => {
  const ownerMap = await fetchOwners();
  return ownerMap[ownerId.toString()] || ownerId.toString();
};

const {
  set_draw_table,
  set_loading,
  set_filters_data,
  set_save_disabled,
  set_save_clicked,
  set_category,
  set_mgmt_audits,
  set_product,
  set_data_edited,
  set_modules
} = managementSlice.actions;

const fetchMgmtAudits = async (
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.getMgmtAudits(lang, filters);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const fetchMngmPrioritization = async (lang: LanguageList) => {
  try {
    const response = await ApiService.getMngmPrioritization(lang);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const fetchAuditSource = async (lang: LanguageList) => {
  try {
    const response = await ApiService.getAuditSource(lang);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const setMgmtAudits =
  (mgmtAudits: IAuditFindingsResult[]) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_mgmt_audits(mgmtAudits));
    } catch (error) {
      console.error(error);
    }
  };

const setCategory =
  (category: ICategoryResult[]) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_category(category));
    } catch (error) {
      console.error(error);
    }
  };

const fetchCategory = async (
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.getCategory(lang, filters);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
const setProduct =
  (product: IProductResult[]) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());
      dispatch(set_product(product));
    } catch (error) {
      console.error(error);
    }
  };

const fetchProduct = async (
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.getProduct(lang, filters);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

const setDrawTable = (draw: boolean) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_draw_table(draw));
  } catch (error) {
    console.error(error);
  }
};

const setFilters = (lang: LanguageList) => async (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    const response = await ApiService.getFilters(lang);

    let fetchedFilters: IFilters[] = [];

    if (response?.data && !Helpers.isObjEmpty(response.data)) {
      fetchedFilters = Object.keys(response.data)
        .filter((key) => {
          const filterOptions = response.data[key];
          return Array.isArray(filterOptions) && filterOptions.length > 0;
        })
        .map((key) => {
          const filterName = key.charAt(0).toUpperCase() + key.slice(1);
          const filterOptions = response.data[key];

          const uniqueOptions = [...new Set(filterOptions)] as (
            | string
            | IEntityProps
          )[];
          const stringOptions = uniqueOptions.filter(
            (option): option is string =>
              Boolean(option) && typeof option === 'string'
          );
          const options = stringOptions.map((option) => ({
            value: option,
            label: option
          }));

          return {
            filter: filterName,
            options,
            value: [] // <-- required by IFilters
          };
        })
        .filter((filter) => filter.options.length > 0);
    }

    dispatch(set_filters_data(fetchedFilters));
  } catch (error) {
    console.error('setFilters error:', error);
    dispatch(set_filters_data([]));
  }
};

const applyFiltersApi = async (
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.applyFilters(lang, filters);

    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const setSaveDisabled = (disabled: boolean) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_save_disabled(disabled));
  } catch (error) {
    console.error(error);
  }
};

const setSaveClicked = (saveClicked: boolean) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_save_clicked(saveClicked));
  } catch (error) {
    console.error(error);
  }
};

const setDataEdited = (edited: boolean) => (dispatch: Dispatch) => {
  dispatch(set_data_edited(edited));
};

const deleteAuditFindings = async (params: { afg_id: number }) => {
  try {
    const response = await ApiService.removeAuditFindings(params);
    const { status, data } = response;
    notification.success({
      message: status,
      description: data.message,
      duration: 20
    });
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const saveAuditFindings = async (
  data: any[],
  lang: LanguageList,
  modifiedRowIds?: number[]
) => {
  try {
    const rowsToProcess = modifiedRowIds
      ? data.filter(
          (item) => item.afg_id === 0 || modifiedRowIds.includes(item.afg_id)
        )
      : data;

    const transformedData = await Promise.all(
      rowsToProcess.map(async (item) => {
        const { afg_id, ...rest } = item;
        const baseData = { afg_id };
        let ownerRef = rest.afg_owner_ref;

        const emailResult = await getOwnerEmail(ownerRef);
        let formattedDate = null;
        if (
          rest.afg_due_date &&
          rest.afg_due_date !== 'None' &&
          rest.afg_due_date !== ''
        ) {
          if (
            typeof rest.afg_due_date === 'string' &&
            rest.afg_due_date.match(/^\d{4}-\d{2}-\d{2}$/)
          ) {
            formattedDate = rest.afg_due_date;
          } else if ((rest.afg_due_date as IDayjsObject)?.$d) {
            formattedDate = formatDateToYYYYMMDD(
              (rest.afg_due_date as IDayjsObject).$d
            );
          } else {
            try {
              formattedDate = formatDateToYYYYMMDD(
                new Date(rest.afg_due_date as string | Date)
              );
            } catch {
              formattedDate = null;
            }
          }
        }

        return {
          ...baseData,
          afg_id: item.afg_id,
          ads_value_en: rest.ads_value_en,
          afg_desc_en: rest.afg_desc_en,
          audit_name: rest.audit_name,
          afg_due_date: formattedDate,
          afg_owner_ref: emailResult,

          pri_value_en: rest.pri_value_en,

          ind_value_en: rest.ind_value_en,
          afg_ind_ref: rest.afg_ind_ref,

          mod_value_en: rest.mod_value_en,
          afg_mod_ref: rest.afg_mod_ref,

          group_id: rest.group_id,
          division_id: rest.division_id,
          region_id: rest.region_id,
          legal_entity_id: rest.legal_entity_id,
          business_unit_id: rest.business_unit_id
        };
      })
    );

    if (transformedData.length === 0) {
      notification.info({
        message: 'Info',
        description: 'No changes to save',
        duration: 20
      });
      return;
    }

    const response = await ApiService.saveAuditFindings(transformedData, lang);
    notification.success({
      message: 'Success',
      description:
        response?.data?.message || 'Audit findings saved successfully',
      duration: 20
    });
    return response;
  } catch (error: unknown) {
    const apiError = error as { response?: { data?: { message?: string } } };
    notification.error({
      message: 'Error',
      description:
        apiError?.response?.data?.message || 'Failed to save audit findings',
      duration: 20
    });
    console.error(error);
  }
};

const getOwnersList = async () => {
  try {
    const response = await ApiService.getOwnersList();
    return response?.data?.ownership || [];
  } catch (error) {
    console.error('Error fetching owners list:', error);
    return [];
  }
};

const setModules = (modules: any[]) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_modules(modules));
  } catch (error) {
    console.error(error);
  }
};

export default {
  fetchMgmtAudits,
  fetchMngmPrioritization,
  fetchAuditSource,
  fetchCategory,
  fetchProduct,
  setDrawTable,
  setFilters,
  applyFiltersApi,
  setSaveClicked,
  getOwnersList,
  setSaveDisabled,
  deleteAuditFindings,
  setCategory,
  setProduct,
  setMgmtAudits,
  setDataEdited,
  saveAuditFindings,
  setModules
};
